<template>
  <div class="notifications-panel">
    <div class="text-h6 mb-2" v-if="selectedNotifications.length > 0">
      <v-layout wrap align-center justify-space-between>
        <div class="mb-2 flex-column mr-2 italic">
          {{ $t("common.notifications") }}
        </div>
        <div class="flex-row">
          <span class="normal">
            {{ selectedNotifications.length }}
            {{ $t("common.selectedAbbreviated") }}
          </span>
        </div>
      </v-layout>
      <div class="flex-row wrap my-3">
        <v-btn
          color="green"
          class="mr-2 mb-2"
          outlined
          @click="markSelectedNotificationsAsSeen(selectedNotifications, true)"
        >
          <F2gIcon
            class="multiple-notifications-icons mr-3"
            value="eye"
            :width="20"
            :height="20"
            :strokeWidth="4"
            stroke="green"
            :tooltip="$t('common.seenSF')"
          />
          <span class="multiple-notifications-text">{{
            $t("common.seenSF")
          }}</span>
        </v-btn>
        <v-btn
          color="green"
          class="mr-2 mb-2"
          outlined
          @click="markSelectedNotificationsAsSeen(selectedNotifications, false)"
        >
          <F2gIcon
            class="multiple-notifications-icons mr-3"
            value="hidden"
            :width="20"
            :height="20"
            :strokeWidth="4"
            stroke="green"
            :tooltip="$t('common.notSeenSF')"
          />
          <span class="multiple-notifications-text">{{
            $t("common.notSeenSF")
          }}</span>
        </v-btn>
        <v-btn
          color="red"
          outlined
          @click="deleteSelectedNotifications(selectedNotifications)"
        >
          <F2gIcon
            class="multiple-notifications-icons mr-3"
            value="delete"
            :width="20"
            :height="20"
            :strokeWidth="4"
            stroke="red"
            :tooltip="$t('common.delete')"
          />
          <span class="multiple-notifications-text">{{
            $t("common.delete")
          }}</span>
        </v-btn>
      </div>
    </div>
    <div v-else class="text-h6 mb-2 italic">
      {{ $t("common.notifications") }}
    </div>
    <div
      class="px-1"
      v-if="!loading"
      :class="{ notifications: !smallSize, 'notifications-small': smallSize }"
    >
      <notification-elem-list
        v-for="notification in notifications.profileNotifications"
        :key="notification.data.sending_time"
        :value="notification"
        :viewed="notification.view"
        :formatTime="!loadingNewNotifications"
        :selected="
          selectedNotifications.find(
            (notificationId) => notificationId === notification.id
          )
        "
        selectionable
        @remove="markAsViewed(notification.id, true)"
        @selected="selectNotification"
      >
        <!-- three dots -->
        <v-menu bottom left transition="slide-y-transition">
          <template v-slot:activator="{ on }">
            <v-btn dark icon v-on="on">
              <F2gIcon :width="15" value="three-dots" />
            </v-btn>
          </template>

          <v-list>
            <!-- Viewed -->
            <v-list-item
              v-show="!notification.view"
              @click="markAsViewed(notification.id, true)"
            >
              <F2gIcon :width="20" value="eye" />
              <span class="mr-2 ml-2">{{ $t("common.seen") }}</span>
            </v-list-item>
            <!-- Not viewed -->
            <v-list-item
              v-show="notification.view"
              @click="markAsViewed(notification.id, false)"
            >
              <F2gIcon :width="20" value="hidden" />
              <span class="mr-2 ml-2">{{ $t("common.notSeen") }}</span>
            </v-list-item>
            <!-- Delete -->
            <v-list-item @click="deleteNotification(notification.id)">
              <F2gIcon :width="20" value="delete" />
              <span class="mr-2 ml-2">{{ $t("common.delete") }}</span>
            </v-list-item>
            <!-- -->
          </v-list>
        </v-menu>
        <!-- end three dots -->
      </notification-elem-list>
      <NoData
        v-if="smallSize"
        class="mt-4 pt-5"
        :type="NoDataEnum.enum().INCIDENCES"
        :title="$t('common.noNotifications')"
        small
      />
      <v-layout justify-center class="progress">
        <v-progress-circular
          v-show="
            notifications.existsMoreProfileNotifications &&
            notifications.profileNotifications.length > 0
          "
          v-observe-visibility="visibilityChanged"
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-layout>
    </div>
    <v-layout v-else justify-center class="progress mt-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-layout>
  </div>
</template>

<script>
import NotificationElemList from "@/flit2go-web-core/components/notifications/notification-elem-list";
import { mapState } from "vuex";
import NoData from "@/flit2go-web-core/components/ui/no-data";
import NoDataEnum from "@/flit2go-web-core/interfaces/no-data-enum";

export default {
  name: "NotificationsPanel",
  components: {
    NotificationElemList,
    NoData,
  },
  data: () => ({
    NoDataEnum,
    loading: true,
    loadingNewNotifications: false,
    pagination: {
      page: 0,
      size: 10,
      sort: "created,DESC",
    },
    selectedNotifications: [],
  }),
  computed: {
    ...mapState(["notifications"]),
    smallSize() {
      return (
        !this.notifications.profileNotifications ||
        this.notifications.profileNotifications.length == 0
      );
    },
  },
  async mounted() {
    await this.loadData();
    this.loading = false;
  },
  methods: {
    visibilityChanged(isVisible) {
      if (isVisible) this.loadData();
    },
    loadData() {
      return new Promise((resolve, reject) => {
        if (this.notifications.existsMoreProfileNotifications) {
          this.loadingNewNotifications = true;
          this.$store
            .dispatch("notifications/loadProfileNotifications", this.pagination)
            .then(() => {
              this.pagination.page++;
              resolve();
            })
            .catch(() => {
              reject();
            })
            .finally(() => {
              this.loadingNewNotifications = false;
            });
        } else {
          resolve();
        }
      });
    },
    selectNotification(notificationId) {
      if (
        !this.selectedNotifications.find((itemId) => itemId === notificationId)
      ) {
        this.selectedNotifications.push(notificationId);
      } else {
        this.selectedNotifications.splice(
          this.selectedNotifications.indexOf(notificationId),
          1
        );
      }
    },
    async markAsViewed(notificationId, value) {
      this.loading = true;
      try {
        await this.$store.dispatch("notifications/markNotiAsViewed", {
          notiId: notificationId,
          viewed: value,
        });
      } finally {
        this.selectedNotifications.splice(
          this.selectedNotifications.indexOf(notificationId),
          1
        );
        this.loading = false;
      }
    },
    async markSelectedNotificationsAsSeen(selectedNotifications, value) {
      this.loading = true;
      try {
        await this.$store.dispatch(
          "notifications/markSelectedNotificationsAsSeen",
          {
            selectedNotifications: selectedNotifications,
            viewed: value,
          }
        );
      } finally {
        this.loading = false;
        this.selectedNotifications = [];
      }
    },
    async deleteNotification(notificationId) {
      this.loading = true;
      try {
        await this.$store.dispatch(
          "notifications/deleteNotification",
          notificationId
        );
        this.$store.dispatch("notifications/loadPersistentBellNotifications");
        this.pagination.page = 0;
        this.$store.commit("notifications/EMPTY_PROFILE_NOTIS");
        await this.loadData();
      } finally {
        this.selectedNotifications.splice(
          this.selectedNotifications.indexOf(notificationId),
          1
        );
        this.loading = false;
      }
    },
    async deleteSelectedNotifications(selectedNotifications) {
      try {
        await this.$store.dispatch(
          "notifications/deleteSelectedNotifications",
          selectedNotifications
        );
        this.$store.dispatch("notifications/loadPersistentBellNotifications");
        this.pagination.page = 0;
        this.$store.commit("notifications/EMPTY_PROFILE_NOTIS");
        await this.loadData();
      } finally {
        this.selectedNotifications = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-panel {
  min-height: 300px;

  .notifications {
    height: 650px;
    overflow-y: auto;

    /* width */
    &::-webkit-scrollbar {
      width: 5px;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: lightgrey;
      border-radius: 30px;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: grey-;
    }
  }

  .multiple-notifications-icons {
    padding-top: 5px;
    padding-bottom: 5px;
    cursor: pointer;
  }

  .multiple-notifications-text {
    font-weight: bold;
    font-style: normal;
  }

  .notifications-small {
    height: 300px;
  }

  .progress {
    height: 40px;
  }

  .v-list > .v-list__tile {
    cursor: pointer;
  }
}
</style>
